import React, { useEffect, useState } from "react";
import { Phone, PhoneOff, PhoneIncoming, Mic, MicOff, Ear, Radio, Users, Loader2 } from "lucide-react";
import { onPhone, getPhone, hangup, toggleMute, answer } from "../lib/softphone";

const secs = (from) => (from ? Math.max(0, Math.round((Date.now() - from) / 1000)) : 0);
const clock = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

const MODE_ICON = { listen: Ear, whisper: Radio, barge: Users, join: Users };

/** Floating call bar. Renders nothing until there is a call to show. */
export default function CallBar() {
  const [p, setP] = useState(getPhone());
  const [, tick] = useState(0);

  useEffect(() => onPhone(setP), []);
  useEffect(() => {
    if (p.state !== "in-call") return;
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [p.state]);

  if (p.state === "idle" && !p.error) return null;

  if (p.state === "idle" && p.error) {
    return (
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] max-w-md w-[calc(100%-2rem)] bg-rose-600 text-white rounded-xl shadow-lg px-4 py-3 text-sm" data-testid="call-error">
        {p.error}
      </div>
    );
  }

  const ModeIcon = MODE_ICON[p.mode] || Phone;
  const label = p.state === "incoming" ? "Incoming call"
    : p.state === "calling" ? (p.mode ? `Connecting — ${p.mode}` : "Calling…")
    : p.mode ? `${p.mode} · live` : "In call";

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] w-[calc(100%-2rem)] max-w-md bg-slate-900 dark:bg-slate-800 text-white rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-3"
      data-testid="call-bar">
      <span className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${
        p.state === "in-call" ? "bg-emerald-500" : "bg-indigo-500 animate-pulse"}`}>
        {p.state === "incoming" ? <PhoneIncoming size={17} /> : <ModeIcon size={17} />}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold truncate">{p.number || "Unknown"}</p>
        <p className="text-xs text-slate-300">
          {label}{p.state === "in-call" ? ` · ${clock(secs(p.since))}` : ""}
        </p>
      </div>
      {p.state === "incoming" && (
        <button onClick={answer} data-testid="call-answer"
          className="h-9 px-3 rounded-full bg-emerald-500 hover:bg-emerald-600 text-sm font-semibold flex items-center gap-1.5">
          <Phone size={15} /> Answer
        </button>
      )}
      {p.state === "in-call" && !p.mode && (
        <button onClick={toggleMute} title={p.muted ? "Unmute" : "Mute"} data-testid="call-mute"
          className={`h-9 w-9 rounded-full flex items-center justify-center ${p.muted ? "bg-amber-500" : "bg-white/15 hover:bg-white/25"}`}>
          {p.muted ? <MicOff size={16} /> : <Mic size={16} />}
        </button>
      )}
      <button onClick={hangup} title="Hang up" data-testid="call-hangup"
        className="h-9 w-9 rounded-full bg-rose-500 hover:bg-rose-600 flex items-center justify-center">
        <PhoneOff size={16} />
      </button>
    </div>
  );
}
